import type { LinearRGB } from '../../../core/color';
import type { Landmark } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import { SURFACE } from '../../mesh/attributes';
import { PATTERN } from '../../../render/patterns/patternIds';
import type { Frame, MeshBuilder } from '../../mesh/meshBuilder';
import { gableRoof } from '../roofSolids';
import { horizontalCap, prismSolid, pyramidOverRing, ringPoints } from '../solids';
import { corners } from './genericBlock';
import { buildVessel } from './churchVessel';
import { edgePanel, openedWall, pointedWindows, rectWall, type MasonryStyles } from './masonry';
import { paintedChapelStyles, type PaintedChapelColors } from './paintedChapels';
import { meshFrameOf } from './planFrame';
import { buildCelleMolenRoof } from './cellezustersRoof';
import { NO_TRACERY, type TracerySink } from './tracery/tracerySink';
import {
  CELLEZUSTERS,
  celleCourtRing,
  celleGardenRects,
  cellePartFrame,
  cellePartRect,
  cellezustersFrame,
  type CellePart,
} from './cellezustersPlan';

type Range = (typeof CELLEZUSTERS.ranges)[number];

function domesticRange(
  builder: MeshBuilder,
  frame: Frame,
  styles: MasonryStyles,
  part: Range
): void {
  const rect = cellePartRect(part),
    ring = corners(rect),
    passage = 'passage' in part && typeof part.passage === 'number' ? part.passage : 0;
  ring.forEach((from, side) => {
    const to = ring[(side + 1) % 4]!,
      end = side % 2 === 1,
      length = end ? part.width : part.length,
      holes: Ring[] = [];
    for (let floor = 0; floor < part.floors; floor++)
      for (let bay = 0; bay < (end ? 2 : part.bays); bay++) {
        const x = ((bay + 0.5) * length) / (end ? 2 : part.bays),
          y = 1.05 + floor * 2.7;
        if (!end && passage && floor === 0 && Math.abs(x - length / 2) < passage / 2 + 0.5)
          continue;
        holes.push([
          [x - 0.4, y],
          [x + 0.4, y],
          [x + 0.4, y + 1.5],
          [x - 0.4, y + 1.5],
        ]);
      }
    // Two lower wall strips and an upper lintel form a real through-opening.
    if (!end && passage) {
      const half = passage / 2,
        mid = length / 2,
        panel = edgePanel(from, to);
      for (const [a, b] of [
        [0, mid - half],
        [mid + half, length],
      ])
        openedWall(
          builder,
          styles,
          frame,
          panel,
          [
            [a!, 0],
            [b!, 0],
            [b!, 2.7],
            [a!, 2.7],
          ],
          holes.filter((h) => h[0]![1] < 2.7 && h[0]![0] >= a! && h[1]![0] <= b!)
        );
      rectWall(
        builder,
        styles,
        frame,
        [from, to],
        [2.7, part.eaves],
        holes.filter((h) => h[0]![1] > 2.7),
        0
      );
    } else rectWall(builder, styles, frame, [from, to], [0, part.eaves], holes);
    if (end)
      openedWall(
        builder,
        styles,
        frame,
        edgePanel(from, to),
        [
          [0, part.eaves],
          [length, part.eaves],
          [length / 2, part.ridge],
        ],
        [
          [
            [length / 2 - 0.22, part.eaves + 0.35],
            [length / 2 + 0.22, part.eaves + 0.35],
            [length / 2 + 0.22, part.eaves + 1.1],
            [length / 2 - 0.22, part.eaves + 1.1],
          ],
        ]
      );
  });
  if (passage) {
    const half = passage / 2;
    for (const [u, sign] of [
      [-half, -1],
      [half, 1],
    ]) {
      const edge =
        sign! < 0
          ? ([
              [u!, rect.v0],
              [u!, rect.v1],
            ] as const)
          : ([
              [u!, rect.v1],
              [u!, rect.v0],
            ] as const);
      rectWall(builder, styles, frame, edge, [0, 2.7]);
    }
    horizontalCap(
      builder,
      styles.wall,
      frame,
      corners({ ...rect, u0: -half, u1: half }),
      2.7,
      false
    );
    horizontalCap(builder, styles.wall, frame, ring, 2.75, true);
  } else horizontalCap(builder, styles.wall, frame, ring, 0.01, true);
  if (part.id === CELLEZUSTERS.roofGables.range) buildCelleMolenRoof(builder, frame, styles);
  else
    gableRoof(builder, styles, frame, {
      ...rect,
      eaveY: part.eaves,
      ridgeY: part.ridge,
      ends: { start: false, end: false },
    });
}

function chapel(builder: MeshBuilder, frame: Frame, styles: MasonryStyles): void {
  const c = CELLEZUSTERS.chapel;
  buildVessel(
    builder,
    styles,
    frame,
    {
      u0: -c.length / 2,
      chordU: c.length / 2,
      vc: 0,
      half: c.width / 2,
      eaveY: c.eaves,
      ridgeY: c.ridge,
      apse: 'flat',
    },
    { longWindows: { sill: 2.1, top: 8.3 }, westWindow: true, bay: c.length / c.bays }
  );
  const ring = ringPoints([6, 0], 0.55, 8, Math.PI / 8);
  ring.forEach((from, i) => {
    const to = ring[(i + 1) % ring.length]!,
      width = Math.hypot(to[0] - from[0], to[1] - from[1]);
    rectWall(
      builder,
      styles,
      frame,
      [from, to],
      [14.3, 16.3],
      pointedWindows(width, width, 0.4, 1.9),
      14.3
    );
  });
  horizontalCap(builder, styles.wall, frame, ring, 14.3, false);
  pyramidOverRing(builder, styles, frame, ring, 16.3, { plan: [6, 0], y: 18.6 });
}

/** Individually recorded street-side masses; no standard four-range monastery fills this plot. */
export function buildCellezusters(
  builder: MeshBuilder,
  landmark: Landmark,
  colors: PaintedChapelColors & { tile: LinearRGB; ground: LinearRGB; garden: LinearRGB },
  ids: { edgeId: number; seed: number },
  tracery: TracerySink = NO_TRACERY
): void {
  const plan = cellezustersFrame(landmark),
    base = paintedChapelStyles(colors, ids, tracery),
    brick = { ...base, wall: { ...base.wall, surface: SURFACE.brick } },
    stylesOf = (part: CellePart) =>
      part.roof === 'slate'
        ? brick
        : {
            ...brick,
            roof: {
              ...brick.roof,
              color: colors.tile,
              surface: SURFACE.roofTile,
              pattern: PATTERN.roofTileHollePan,
            },
          };
  chapel(
    builder,
    meshFrameOf(cellePartFrame(plan, CELLEZUSTERS.chapel)),
    stylesOf(CELLEZUSTERS.chapel)
  );
  for (const part of CELLEZUSTERS.ranges)
    domesticRange(builder, meshFrameOf(cellePartFrame(plan, part)), stylesOf(part), part);
  const frame = meshFrameOf(plan),
    ground = { ...brick.wall, color: colors.ground, surface: SURFACE.street };
  prismSolid(builder, ground, frame, celleCourtRing(), 0, 0.03, { top: true, bottom: false });
  const hospital = CELLEZUSTERS.ranges.find((r) => r.id === CELLEZUSTERS.entryRange)!;
  horizontalCap(
    builder,
    ground,
    meshFrameOf(cellePartFrame(plan, hospital)),
    corners(CELLEZUSTERS.entry),
    0.03,
    true
  );
  for (const rect of celleGardenRects()) {
    horizontalCap(
      builder,
      { ...ground, color: colors.garden, surface: SURFACE.garden },
      frame,
      corners(rect),
      0.035,
      true
    );
  }
}
